// FEATURES-1 fixtures for `dev:mock` (docs/specs/features-1.md). A small in-memory mirror of the
// backend registry (`backend/src/scribe/features.py`) so the admin Features card and
// `useFeatureEnabled` can be exercised with no backend: two features "deployed", the rest
// waiting on infrastructure. Switch changes persist for the page's lifetime only.
import type { AdminFeature, AdminFeatureList, FeatureMap, UpdateFeatureSwitchRequest } from '@scribe/core/types';

const base: AdminFeature[] = [
  {
    key: 'assistant',
    name: 'Assistant',
    description: 'Grounded, cited Q&A over your meetings.',
    infrastructure: 'deployed',
    infra_flag: 'enable_assistant',
    enabled: false,
    source: 'default',
    cost_estimate: 'Pay per question (Bedrock tokens, counted against budgets). ~$0 idle.',
    confirm_note: "Every question spends Bedrock tokens, billed to the asker's budget. Takes effect within about a minute.",
    last_change: null,
  },
  {
    key: 'bug_reports',
    name: 'In-app bug reports',
    description: 'Users send a bug report with diagnostics, never transcript text, titles or emails.',
    infrastructure: 'deployed',
    infra_flag: 'enable_bug_reports',
    enabled: true,
    source: 'admin',
    cost_estimate: '~$0. Database rows, 180-day cleanup.',
    confirm_note: null,
    last_change: {
      enabled: true,
      at: '2026-10-01T15:00:00.000Z',
      actor_user_id: 'user-1',
      actor_display_name: 'Sample Admin',
      reason: 'Pilot with the team',
    },
  },
  {
    key: 'otter_import',
    name: 'Otter import',
    description: 'Users import their Otter meeting history into Scribe.',
    infrastructure: 'not_deployed',
    infra_flag: 'enable_otter_import',
    enabled: false,
    source: 'default',
    cost_estimate: '~$0 idle. Import worker per run; summaries only on first open.',
    confirm_note: null,
    last_change: null,
  },
  {
    key: 'live_silence_gating',
    name: 'Live silence gating',
    description: "Don't send silence to Transcribe Streaming. Recorded audio is unaffected.",
    infrastructure: 'not_deployed',
    infra_flag: 'enable_live_silence_gating',
    enabled: false,
    source: 'default',
    cost_estimate: 'Lowers Streaming spend. No extra infrastructure cost.',
    confirm_note: null,
    last_change: null,
  },
  {
    key: 'silence_trim',
    name: 'Silence trimming',
    description: 'Cut long silences before batch transcription. Words never merge; playback keeps the original.',
    infrastructure: 'not_deployed',
    infra_flag: 'enable_silence_trim',
    enabled: false,
    source: 'default',
    cost_estimate: 'Lowers Transcribe batch spend. Small compute per recording.',
    confirm_note: null,
    last_change: null,
  },
  {
    key: 'whisper',
    name: 'Whisper engine',
    description: 'Self-hosted Whisper as a per-recording engine choice, next to AWS Transcribe.',
    infrastructure: 'not_deployed',
    infra_flag: 'enable_whisper',
    enabled: false,
    source: 'default',
    cost_estimate: 'GPU workers, est. $0.02-0.06 per audio hour plus any idle capacity.',
    confirm_note: 'Users can send recordings to GPU workers. Cost depends on how many choose Whisper.',
    last_change: null,
  },
  {
    key: 'voice_prints',
    name: 'Voice prints',
    description: 'Users opt in with their own voice; new recordings name matching speakers automatically.',
    infrastructure: 'deployed',
    infra_flag: 'enable_voice_prints',
    enabled: false,
    source: 'default',
    cost_estimate: '~$1/mo. A few seconds of Lambda per recording; prints are ~1 KB each.',
    confirm_note:
      'Voice prints are biometric data. Each person opts in for themselves and can delete their print at any time. Check the consent wording with legal before switching on for everyone.',
    last_change: null,
  },
];

let mockFeatureState: AdminFeature[] = base.map((f) => ({ ...f }));

export const mockFeatures = {
  listAdminFeatures: (): AdminFeatureList => ({ features: mockFeatureState.map((f) => ({ ...f })) }),
  updateFeatureSwitch: (key: string, body: UpdateFeatureSwitchRequest): AdminFeature => {
    const feature = mockFeatureState.find((f) => f.key === key);
    if (!feature) throw new Error('Unknown feature');
    if (feature.infrastructure !== 'deployed') throw new Error(`${feature.name} has no infrastructure in this environment.`);
    const updated: AdminFeature = {
      ...feature,
      enabled: body.enabled,
      source: 'admin',
      last_change: {
        enabled: body.enabled,
        at: new Date().toISOString(),
        actor_user_id: 'user-1',
        actor_display_name: 'You (mock)',
        reason: body.reason ?? null,
      },
    };
    mockFeatureState = mockFeatureState.map((f) => (f.key === key ? updated : f));
    return updated;
  },
  listFeatures: (): FeatureMap =>
    Object.fromEntries(mockFeatureState.map((f) => [f.key, f.infrastructure === 'deployed' && f.enabled])),
  /** Test-only: restore the fixtures. */
  reset: () => {
    mockFeatureState = base.map((f) => ({ ...f }));
  },
};
